import { useTranslation } from 'react-i18next';
import { PartnerOnboardingCollaborator, withStyles } from 'vopay';
import { BodyText, Button, Card, Title } from 'UI';
import useOnboardingTemplateName from '@/hooks/useOnboardingTemplateName';
import styles from './CollaboratorCard.module.scss';

const cn = withStyles(styles);

type CollaboratorCardProps = {
  collaborator: PartnerOnboardingCollaborator;
  /** Viewer, another drawer action in flight, or a link that isn't Active: Resend is disabled. */
  isResendDisabled: boolean;
  /** False when the account's owning partner emails its own accounts (Partner Emails Account): no Resend (ID-14303). */
  isOnboardingEmailAllowed: boolean;
  onResendClick: (collaborator: PartnerOnboardingCollaborator) => void;
};

/** Figma `1583:5786`: one invited collaborator on the Collaborators tab. */
export default function CollaboratorCard({
  collaborator,
  isResendDisabled,
  isOnboardingEmailAllowed,
  onResendClick,
}: CollaboratorCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'OnboardingTracker.ApplicationProgress.CollaboratorCard' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { resolveFormName, resolveDocumentName } = useOnboardingTemplateName();

  // Sections have no ID and can repeat, so each display name is listed once per kind and keys its bullet.
  const formNames = [...new Set(collaborator.Forms.map(({ Identifier, Name }) => resolveFormName(Identifier, Name)))];

  const documentNames = [
    ...new Set(collaborator.Documents.map(({ Identifier, Name }) => resolveDocumentName(Identifier, Name))),
  ];

  return (
    <Card variant="section" className={cn('card')} data-testid="collaboratorCard">
      <div className={cn('identity')}>
        <Title level="4">{collaborator.Name}</Title>

        {isOnboardingEmailAllowed && (
          <Button size="small" disabled={isResendDisabled} onClick={() => onResendClick(collaborator)}>
            {t('resend', { defaultValue: 'Resend' })}
          </Button>
        )}
      </div>

      <div className={cn('row', 'emailRow')}>
        <BodyText weight="md" className={cn('label')}>
          {tGlobal('email')}
        </BodyText>

        <BodyText className={cn('value')}>{collaborator.Email}</BodyText>
      </div>

      <div className={cn('row')}>
        <BodyText weight="md" className={cn('label')}>
          {t('assigned', { defaultValue: 'Assigned' })}
        </BodyText>

        {/* BodyText carries Body/16/Regular and the colour down to each item and its bullet. */}
        <BodyText className={cn('value')}>
          <ul className={cn('assignedList')}>
            {formNames.map((name) => (
              <li key={`form-${name}`}>{name}</li>
            ))}

            {documentNames.map((name) => (
              <li key={`document-${name}`}>{name}</li>
            ))}
          </ul>
        </BodyText>
      </div>
    </Card>
  );
}
